import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Printer, ArrowLeft } from 'lucide-react';
import api from '../components/api';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';

// Composants refactorisés
import PrintHeader from '../components/emplois/PrintHeader';
import EmploymentInfoTable from '../components/emplois/EmploymentInfoTable';
import ScheduleTable from '../components/emplois/ScheduleTable';
import PrintScheduleTable from '../components/emplois/PrintScheduleTable';
import EmploymentStats from '../components/emplois/EmploymentStats';

const DetailEmplois = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [emploi, setEmploi] = useState(null);
  const [etablissement, setEtablissement] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [emploiResponse, etablissementResponse] = await Promise.all([
          api.get(`/emplois/${id}/`),
          api.get('etablissement/etablissement-infos/')
        ]);
        setEmploi(emploiResponse.data);
        setEtablissement(etablissementResponse.data);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const formatTime = (time) => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!emploi || !etablissement) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-gray-900">Emploi du temps non trouvé</h1>
            <p className="mt-2 text-gray-600">L'emploi du temps que vous recherchez n'existe pas ou a été supprimé.</p>
            <button
              onClick={() => navigate('/liste-emplois')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 print:p-0 print:m-0">
        <div id="emploi-details" className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden print:border-0 print:shadow-none print:rounded-none">
          
          {/* En-tête d'écran */}
          <div className="bg-gradient-to-r from-[#202654] to-[#2a2f6c] p-6 flex justify-between items-center print:hidden">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/liste-emplois')}
                className="text-white hover:bg-white/10 p-2 rounded-full transition-colors"
                aria-label="Retour à la liste"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h1 className="text-2xl font-bold text-white">Détails de l'Emploi du Temps</h1>
            </div>
            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center space-x-2 bg-white/10 px-3 py-1.5 rounded-lg">
                <Calendar className="h-4 w-4 text-white/80" />
                <span className="text-sm text-white/90">{emploi.annee?.libelle || 'Année académique'}</span>
              </div>
              <button
                onClick={handlePrint}
                className="inline-flex items-center px-4 py-2 bg-white text-[#202654] font-medium rounded-md hover:bg-white/90 transition-colors shadow-sm"
                aria-label="Imprimer l'emploi du temps"
              >
                <Printer className="h-5 w-5 mr-2" />
                Imprimer
              </button>
            </div>
          </div>

          {/* Contenu principal */}
          <div className="p-6 print:p-0">
            
            {/* En-tête d'impression */}
            <PrintHeader etablissement={etablissement} emploi={emploi} />

            {/* Informations principales */}
            <EmploymentInfoTable emploi={emploi} />

            {/* Statistiques (écran seulement) */}
            <EmploymentStats emploi={emploi} />

            {/* Tableau des cours pour l'écran */}
            <ScheduleTable emploi={emploi} formatTime={formatTime} />

            {/* Tableau des cours pour l'impression */}
            <PrintScheduleTable emploi={emploi} formatTime={formatTime} />

          </div>
        </div>
      </div>
      <div className="hidden print:block mt-8"></div>
    </Layout>
  );
};

export default DetailEmplois; 